/** @jsxRuntime classic */
/** @jsx jsx */

import React, { Fragment, useMemo } from 'react';
import { jsx } from '@emotion/react';
import dayjs from 'dayjs';
import { spaceColumnContainer } from '../../styles';
import { IDailyViewProps } from '../../types';
import { spaceWidth } from '../styles';
import { CurrentTimeLine } from './CurrentTimeLine';
import { useResource } from '../../hooks/useResource';
import { DayXToDrop } from './DayXToDrop';
import { DateTimeFormats, TimeUnit } from '@bondsports/date-time';
import { useResourceSlotsByDate } from '../../hooks/useResourceSlotsByDate';
import { useResourceSlots } from '../../hooks/useResourceSlots';

interface IDayXViewProps extends IDailyViewProps {
	daysCount?: number;
	resourceId: number; // When provided, shows only this specific resource (like Monthly view)
}

/**
 * DayXView component for displaying multiple days with resources
 *
 * @param resourceId - Optional. When provided, shows only this specific resource (similar to Monthly view)
 * @param resourceGroups - Optional. When resourceId is provided, this is ignored and all resources are shown
 * @param daysCount - Number of days to display (default: 1)
 *
 * Usage examples:
 * - Multi-resource mode: <DayXView resourceGroups={groups} daysCount={7} />
 * - Single-resource mode: <DayXView resourceId={123} daysCount={7} />
 */
export const DayXView: React.FC<IDayXViewProps> = ({
	EventComponent,
	isModeChanged,
	onAddNewSlotClick,
	hourSize,
	isLoading,
	currentDate,
	daysCount = 1,
	resourceId,
}) => {
	const { resource } = useResource({ resourceId: Number(resourceId) });
	const { slotsByDate } = useResourceSlotsByDate({ resourceId: Number(resourceId) });
	const { updateSlotData } = useResourceSlots({ resourceId: Number(resourceId) });

	const dates: string[] = useMemo(() => {
		const startDate: dayjs.Dayjs = dayjs(currentDate);
		return Array.from({ length: daysCount }, (_, i) =>
			startDate.add(i, TimeUnit.DAY).format(DateTimeFormats.YYYY_MM_DD)
		);
	}, [currentDate, daysCount]);

	return (
		<Fragment key="DayXView">
			{resource &&
				dates.map((date: string, index: number) => (
					<div
						key={`DayXView-Space-${resource.id}-${index}`}
						css={[spaceColumnContainer(dates.length, 1)]}
						data-aid="DayXView-daily"
					>
						<DayXToDrop
							id={`${String(resourceId)}_${date}`}
							EventComponent={EventComponent}
							withoutChildren
							isLoading={isLoading}
							isModeChanged={isModeChanged}
							onAddNewSlotClick={onAddNewSlotClick}
							currentDate={date}
							activityTimes={resource.activityTimes}
							updateSlotData={updateSlotData}
							slots={slotsByDate.get(date) || []}
						/>
					</div>
				))}
			<CurrentTimeLine width={dates.length * spaceWidth} />
		</Fragment>
	);
};

DayXView.displayName = 'DayXView';
